import { useEffect, useRef, useImperativeHandle, useMemo, useState } from 'react';
import { useGroupKey, useJoinKeyPathToString, useNoOutlineWhenFocused, useRegisterInlineFieldEditor, useWasUndoRedoEventWithData, getDataTokenLocale, parseFormatterAttributes } from '../lib/InlineFieldEditingHooks';
import { DateInput } from 'foundations-components/transitional/components/DateInput';
import { fromMoment, toMoment, now } from 'foundations-components/transitional/utils/SimpleDate';
import { getLocaleDateFormatter } from '../lib/localeUtils';
import { Popover } from 'foundations-components/transitional/components/Popover';
import I18n from 'I18n';
import { jsx as _jsx } from "react/jsx-runtime";
const DateFieldEditor = ({
  name,
  node,
  onUpdate,
  currentValue,
  keyPath,
  moduleName = '',
  onFocusCallback,
  onBlurCallback,
  registerInlineFieldRef
}) => {
  const dateRef = useRef(null);
  useNoOutlineWhenFocused(node);
  useRegisterInlineFieldEditor(moduleName);
  const fieldKeyString = useJoinKeyPathToString(keyPath);
  const groupKey = useGroupKey(moduleName, fieldKeyString, name);
  const [isPickerVisible, setIsPickerVisible] = useState(false);
  // Memoize locale and separators since they don't change during component lifecycle
  const locale = useMemo(() => getDataTokenLocale(node), [node]);
  const formatterAttributes = useMemo(() => parseFormatterAttributes(node), [node]);
  const quoteTimezone = (formatterAttributes === null || formatterAttributes === void 0 ? void 0 : formatterAttributes.formatArgs[0]) || 'UTC';
  const dateFormatter = useMemo(() => getLocaleDateFormatter(locale, quoteTimezone), [locale, quoteTimezone]);
  const dateToDisplay = useMemo(() => {
    if (!currentValue) return undefined;
    return dateFormatter(new Date(Number(currentValue)));
  }, [currentValue, dateFormatter]);
  const wasUndoRedo = useWasUndoRedoEventWithData(currentValue);
  useImperativeHandle(dateRef, () => {
    return {
      focus: () => {
        if (document.activeElement !== node) {
          node.focus();
          onFocusCallback === null || onFocusCallback === void 0 || onFocusCallback();
        }
      }
    };
  }, [node, onFocusCallback]);

  // @TODO: This may fall apart once we have modules with inline & non-inline fields
  // that experience a live update via Remy (because the ref depends on the module DOM node)
  useEffect(() => {
    registerInlineFieldRef === null || registerInlineFieldRef === void 0 || registerInlineFieldRef(name, 'date', dateRef.current);
  }, [registerInlineFieldRef, name]);
  useEffect(() => {
    const onClick = e => {
      e.preventDefault();
      e.stopPropagation();
      setIsPickerVisible(true);
      node.focus();
      onFocusCallback === null || onFocusCallback === void 0 || onFocusCallback();
    };
    const onBlur = () => {
      if (!isPickerVisible) {
        onBlurCallback === null || onBlurCallback === void 0 || onBlurCallback();
      }
    };
    const onKeyDown = e => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onClick(e);
      }
    };
    node.addEventListener('click', onClick);
    node.addEventListener('blur', onBlur);
    node.addEventListener('keydown', onKeyDown);
    node.setAttribute('tabindex', '0');
    node.style.cursor = 'pointer';
    return () => {
      node.removeEventListener('click', onClick);
      node.removeEventListener('blur', onBlur);
      node.removeEventListener('keydown', onKeyDown);
      node.removeAttribute('tabindex');
      node.style.cursor = '';
    };
  }, [node, onFocusCallback, onBlurCallback, isPickerVisible]);

  // Update DOM display
  useEffect(() => {
    if (document.activeElement !== node || wasUndoRedo) {
      if (node.innerText !== dateToDisplay) {
        node.innerText = dateToDisplay || I18n.text('inpage.inlineFieldEditors.placeholder.date');
        node.style.color = dateToDisplay ? '' : '#999';
        node.style.fontStyle = dateToDisplay ? '' : 'italic';
      }
    }
  }, [dateToDisplay, node, wasUndoRedo]);
  const handleDateChange = e => {
    setIsPickerVisible(false);
    const newDate = e.target.value;
    if (onUpdate && newDate) {
      const newValue = toMoment(newDate).valueOf();
      if (currentValue !== newValue) {
        onUpdate(keyPath, String(newValue), {
          groupKey
        });
      }
    }
    onBlurCallback === null || onBlurCallback === void 0 || onBlurCallback();
  };
  const handleOpenChange = evt => {
    const open = evt.target.value;
    setIsPickerVisible(open);
    if (!open) {
      onBlurCallback === null || onBlurCallback === void 0 || onBlurCallback();
    }
  };

  // Hardcoded for CPQ use case
  const today = now();
  const max = addYears(today, 1);
  return /*#__PURE__*/_jsx(Popover, {
    target: node,
    open: isPickerVisible,
    placement: "bottom",
    content: /*#__PURE__*/_jsx(DateInput, {
      format: "ll",
      value: currentValue ? fromMoment(I18n.moment(Number(currentValue))) : undefined,
      onChange: handleDateChange,
      autoFocus: true,
      max: max,
      min: today
    }),
    onOpenChange: handleOpenChange,
    closeOnOutsideClick: true
  });
};
function addYears(date, years) {
  return {
    year: date.year + years,
    month: date.month,
    date: date.date
  };
}
export default DateFieldEditor;